import { useState } from 'react';
import { Home, Car, CreditCard, GraduationCap, Briefcase, Building, TrendingDown, Clock, CheckCircle, ArrowRight, Calculator, Award, Shield, Zap, Phone } from 'lucide-react';

export default function LoanCard({ title, rate }) {
  const [isHovered, setIsHovered] = useState(false);

  // Navigation function for hash routing
  const navigateTo = (path) => {
    window.location.hash = path;
  };

  const loanIcons = {
    'Home Loan': <Home className="w-8 h-8" />,
    'Car Loan': <Car className="w-8 h-8" />,
    'Education Loan': <GraduationCap className="w-8 h-8" />,
    'Business Loan': <Briefcase className="w-8 h-8" />,
    'Property Loan': <Building className="w-8 h-8" />
  };

  const loanDetails = {
    'Home Loan': {
      description: 'Finance your dream home with lowest interest rates',
      maxAmount: '₹2 Crore',
      tenure: 'Up to 30 years',
      processing: '0.5%',
      gradient: 'from-blue-700 to-blue-900',
      bgGradient: 'from-blue-50 to-blue-100',
      features: ['Zero prepayment charges', 'Doorstep service', 'Quick approval']
    },
    'Car Loan': {
      description: 'Drive your dream car home today with easy EMIs',
      maxAmount: '₹50 Lakhs',
      tenure: 'Up to 7 years',
      processing: '1%',
      gradient: 'from-blue-700 to-blue-900',
      bgGradient: 'from-blue-50 to-blue-100',
      features: ['New & used cars', 'Minimal documentation', '90% funding']
    },
    'Education Loan': {
      description: 'Invest in your future with education financing',
      maxAmount: '₹1 Crore',
      tenure: 'Up to 15 years',
      processing: '1%',
      gradient: 'from-blue-700 to-blue-900',
      bgGradient: 'from-blue-50 to-blue-100',
      features: ['Study abroad', 'Moratorium period', 'Tax benefits']
    },
    'Business Loan': {
      description: 'Grow your business with flexible funding',
      maxAmount: '₹5 Crore',
      tenure: 'Up to 10 years',
      processing: '1.5%',
      gradient: 'from-blue-700 to-blue-900',
      bgGradient: 'from-blue-50 to-blue-100',
      features: ['Working capital', 'Business expansion', 'No collateral required']
    },
    'Property Loan': {
      description: 'Unlock the value of your property',
      maxAmount: '₹10 Crore',
      tenure: 'Up to 20 years',
      processing: '1%',
      gradient: 'from-blue-700 to-blue-900',
      bgGradient: 'from-blue-50 to-blue-100',
      features: ['Against property', 'Multiple end-use', 'High loan amount']
    }
  };

  const details = loanDetails[title] || loanDetails['Business Loan'];
  const icon = loanIcons[title] || <Briefcase className="w-8 h-8" />;

  return (
    <div 
      className="group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Decorative Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${details.bgGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
      
      {/* Top Accent Line */}
      <div className={`h-1.5 bg-gradient-to-r ${details.gradient}`}></div>
      
      <div className="relative p-6">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-4">
          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${details.gradient} flex items-center justify-center text-white shadow-lg transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
            {icon}
          </div>
          
          {/* Popular Badge */}
          {rate <= 9 && (
            <div className="flex items-center space-x-1 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold animate-pulse">
              <Award className="w-3 h-3" />
              <span>Popular</span>
            </div>
          )}
        </div>

        {/* Title & Description */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
          <p className="text-sm text-gray-600 leading-relaxed">{details.description}</p>
        </div>

        {/* Interest Rate Highlight */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 mb-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 mb-1">Interest Rate Starting From</p>
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-bold text-gray-900">{rate}%</span>
                <span className="text-sm text-gray-600">per annum</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Key Details Grid */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center hover:border-blue-300 transition-colors duration-200">
            <p className="text-xs text-gray-600 mb-1">Max Amount</p>
            <p className="text-sm font-bold text-gray-900">{details.maxAmount}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center hover:border-blue-300 transition-colors duration-200">
            <p className="text-xs text-gray-600 mb-1">Tenure</p>
            <p className="text-sm font-bold text-gray-900">{details.tenure}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3 text-center hover:border-blue-300 transition-colors duration-200">
            <p className="text-xs text-gray-600 mb-1">Processing</p>
            <p className="text-sm font-bold text-gray-900">{details.processing}</p>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-2 mb-6">
          {details.features.map((feature, index) => (
            <div 
              key={index}
              className="flex items-center space-x-2 text-sm text-gray-700 transform transition-all duration-300"
              style={{
                opacity: isHovered ? 1 : 0.7,
                transform: isHovered ? 'translateX(4px)' : 'translateX(0)',
                transitionDelay: `${index * 50}ms`
              }}
            >
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Quick Info Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex items-center space-x-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
            <Zap className="w-3 h-3" />
            <span>Quick Approval</span>
          </div>
          <div className="flex items-center space-x-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
            <Shield className="w-3 h-3" />
            <span>100% Secure</span>
          </div>
          <div className="flex items-center space-x-1 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            <span>24hr Service</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-3">
          <button 
            onClick={() => navigateTo('calculator')}
            className={`w-full bg-gradient-to-r ${details.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 group`}
          >
            <Calculator className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
            <span>Calculate EMI</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          
          <button 
            onClick={() => navigateTo('contact')}
            className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 flex items-center justify-center space-x-2"
          >
            <Phone className="w-4 h-4" />
            <span>Contact Us</span>
          </button>
        </div>

        {/* Trust Indicator */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-2 text-xs text-gray-600">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span>RBI Approved • Trusted by 50,000+ customers</span>
          </div>
        </div>
      </div>

      {/* Hover Glow Effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${details.gradient} rounded-2xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-500 -z-10`}></div>
    </div>
  );
}
